import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import CookieConsent from "@/components/CookieConsent";
import Chatbot from "@/components/Chatbot";

// Critical pages loaded immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Admin pages (lazy loaded)
const Login = lazy(() => import("./pages/admin/Login"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const BlogAdmin = lazy(() => import("./pages/admin/BlogAdmin"));
const TestimonialsAdmin = lazy(() => import("./pages/admin/TestimonialsAdmin"));
const ServicesAdmin = lazy(() => import("./pages/admin/ServicesAdmin"));
const AreasAdmin = lazy(() => import("./pages/admin/AreasAdmin"));
const ContentAdmin = lazy(() => import("./pages/admin/ContentAdmin"));

// Lazy load non-critical pages to reduce initial bundle
const Berkshire = lazy(() => import("./pages/Berkshire"));
const Hampshire = lazy(() => import("./pages/Hampshire"));
const Surrey = lazy(() => import("./pages/Surrey"));
const Servicing = lazy(() => import("./pages/Servicing"));
const Repairs = lazy(() => import("./pages/Repairs"));
const Automation = lazy(() => import("./pages/Automation"));
const SmartController = lazy(() => import("./pages/SmartController"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const AboutUs = lazy(() => import("./pages/AboutUs"));

// ==================== BERKSHIRE TOWNS ====================
// Ascot
const AscotRepairs = lazy(() => import("./pages/towns/AscotRepairs"));
const AscotServicing = lazy(() => import("./pages/towns/AscotServicing"));
const AscotAutomation = lazy(() => import("./pages/towns/AscotAutomation"));

// Bracknell
const BracknellRepairs = lazy(() => import("./pages/towns/BracknellRepairs"));
const BracknellServicing = lazy(() => import("./pages/towns/BracknellServicing"));
const BracknellAutomation = lazy(() => import("./pages/towns/BracknellAutomation"));

// Burnham
const BurnhamRepairs = lazy(() => import("./pages/towns/BurnhamRepairs"));
const BurnhamServicing = lazy(() => import("./pages/towns/BurnhamServicing"));
const BurnhamAutomation = lazy(() => import("./pages/towns/BurnhamAutomation"));

// Calcot
const CalcotRepairs = lazy(() => import("./pages/towns/CalcotRepairs"));
const CalcotServicing = lazy(() => import("./pages/towns/CalcotServicing"));
const CalcotAutomation = lazy(() => import("./pages/towns/CalcotAutomation"));

// Caversham
const CavershamRepairs = lazy(() => import("./pages/towns/CavershamRepairs"));
const CavershamServicing = lazy(() => import("./pages/towns/CavershamServicing"));
const CavershamAutomation = lazy(() => import("./pages/towns/CavershamAutomation"));

// Cookham
const CookhamRepairs = lazy(() => import("./pages/towns/CookhamRepairs"));
const CookhamServicing = lazy(() => import("./pages/towns/CookhamServicing"));
const CookhamAutomation = lazy(() => import("./pages/towns/CookhamAutomation"));

// Crowthorne
const CrowthorneRepairs = lazy(() => import("./pages/towns/CrowthorneRepairs"));
const CrowthorneServicing = lazy(() => import("./pages/towns/CrowthorneServicing"));
const CrowthorneAutomation = lazy(() => import("./pages/towns/CrowthorneAutomation"));

// Datchet
const DatchetRepairs = lazy(() => import("./pages/towns/DatchetRepairs"));
const DatchetServicing = lazy(() => import("./pages/towns/DatchetServicing"));
const DatchetAutomation = lazy(() => import("./pages/towns/DatchetAutomation"));

// Earley
const EarleyRepairs = lazy(() => import("./pages/towns/EarleyRepairs"));
const EarleyServicing = lazy(() => import("./pages/towns/EarleyServicing"));
const EarleyAutomation = lazy(() => import("./pages/towns/EarleyAutomation"));

// Finchampstead
const FinchampsteadRepairs = lazy(() => import("./pages/towns/FinchampsteadRepairs"));
const FinchampsteadServicing = lazy(() => import("./pages/towns/FinchampsteadServicing"));
const FinchampsteadAutomation = lazy(() => import("./pages/towns/FinchampsteadAutomation"));

// Hungerford
const HungerfordRepairs = lazy(() => import("./pages/towns/HungerfordRepairs"));
const HungerfordServicing = lazy(() => import("./pages/towns/HungerfordServicing"));
const HungerfordAutomation = lazy(() => import("./pages/towns/HungerfordAutomation"));

// Iver
const IverRepairs = lazy(() => import("./pages/towns/IverRepairs"));
const IverServicing = lazy(() => import("./pages/towns/IverServicing"));
const IverAutomation = lazy(() => import("./pages/towns/IverAutomation"));

// Langley
const LangleyRepairs = lazy(() => import("./pages/towns/LangleyRepairs"));
const LangleyServicing = lazy(() => import("./pages/towns/LangleyServicing"));
const LangleyAutomation = lazy(() => import("./pages/towns/LangleyAutomation"));

// Maidenhead
const MaidenheadRepairs = lazy(() => import("./pages/towns/MaidenheadRepairs"));
const MaidenheadServicing = lazy(() => import("./pages/towns/MaidenheadServicing"));
const MaidenheadAutomation = lazy(() => import("./pages/towns/MaidenheadAutomation"));

// Marlow
const MarlowRepairs = lazy(() => import("./pages/towns/MarlowRepairs"));
const MarlowServicing = lazy(() => import("./pages/towns/MarlowServicing"));
const MarlowAutomation = lazy(() => import("./pages/towns/MarlowAutomation"));

// Newbury
const NewburyRepairs = lazy(() => import("./pages/towns/NewburyRepairs"));
const NewburyServicing = lazy(() => import("./pages/towns/NewburyServicing"));
const NewburyAutomation = lazy(() => import("./pages/towns/NewburyAutomation"));

// Pangbourne
const PangbourneRepairs = lazy(() => import("./pages/towns/PangbourneRepairs"));
const PangbourneServicing = lazy(() => import("./pages/towns/PangbourneServicing"));
const PangbourneAutomation = lazy(() => import("./pages/towns/PangbourneAutomation"));

// Reading
const ReadingRepairs = lazy(() => import("./pages/towns/ReadingRepairs"));
const ReadingServicing = lazy(() => import("./pages/towns/ReadingServicing"));
const ReadingAutomation = lazy(() => import("./pages/towns/ReadingAutomation"));

// Sandhurst
const SandhurstRepairs = lazy(() => import("./pages/towns/SandhurstRepairs"));
const SandhurstServicing = lazy(() => import("./pages/towns/SandhurstServicing"));
const SandhurstAutomation = lazy(() => import("./pages/towns/SandhurstAutomation"));

// Slough
const SloughRepairs = lazy(() => import("./pages/towns/SloughRepairs"));
const SloughServicing = lazy(() => import("./pages/towns/SloughServicing"));
const SloughAutomation = lazy(() => import("./pages/towns/SloughAutomation"));

// Sunningdale
const SunningdaleRepairs = lazy(() => import("./pages/towns/SunningdaleRepairs"));
const SunningdaleServicing = lazy(() => import("./pages/towns/SunningdaleServicing"));
const SunningdaleAutomation = lazy(() => import("./pages/towns/SunningdaleAutomation"));

// Sunninghill
const SunninghillRepairs = lazy(() => import("./pages/towns/SunninghillRepairs"));
const SunninghillServicing = lazy(() => import("./pages/towns/SunninghillServicing"));
const SunninghillAutomation = lazy(() => import("./pages/towns/SunninghillAutomation"));

// Thatcham
const ThatchamRepairs = lazy(() => import("./pages/towns/ThatchamRepairs"));
const ThatchamServicing = lazy(() => import("./pages/towns/ThatchamServicing"));
const ThatchamAutomation = lazy(() => import("./pages/towns/ThatchamAutomation"));

// Theale
const ThealeRepairs = lazy(() => import("./pages/towns/ThealeRepairs"));
const ThealeServicing = lazy(() => import("./pages/towns/ThealeServicing"));
const ThealeAutomation = lazy(() => import("./pages/towns/ThealeAutomation"));

// Tilehurst
const TilehurstRepairs = lazy(() => import("./pages/towns/TilehurstRepairs"));
const TilehurstServicing = lazy(() => import("./pages/towns/TilehurstServicing"));
const TilehurstAutomation = lazy(() => import("./pages/towns/TilehurstAutomation"));

// Twyford
const TwyfordRepairs = lazy(() => import("./pages/towns/TwyfordRepairs"));
const TwyfordServicing = lazy(() => import("./pages/towns/TwyfordServicing"));
const TwyfordAutomation = lazy(() => import("./pages/towns/TwyfordAutomation"));

// Warfield
const WarfieldRepairs = lazy(() => import("./pages/towns/WarfieldRepairs"));
const WarfieldServicing = lazy(() => import("./pages/towns/WarfieldServicing"));
const WarfieldAutomation = lazy(() => import("./pages/towns/WarfieldAutomation"));

// Windsor
const WindsorRepairs = lazy(() => import("./pages/towns/WindsorRepairs"));
const WindsorServicing = lazy(() => import("./pages/towns/WindsorServicing"));
const WindsorAutomation = lazy(() => import("./pages/towns/WindsorAutomation"));

// Wokingham
const WokinghamRepairs = lazy(() => import("./pages/towns/WokinghamRepairs"));
const WokinghamServicing = lazy(() => import("./pages/towns/WokinghamServicing"));
const WokinghamAutomation = lazy(() => import("./pages/towns/WokinghamAutomation"));

// Woodley
const WoodleyRepairs = lazy(() => import("./pages/towns/WoodleyRepairs"));
const WoodleyServicing = lazy(() => import("./pages/towns/WoodleyServicing"));
const WoodleyAutomation = lazy(() => import("./pages/towns/WoodleyAutomation"));

// ==================== HAMPSHIRE TOWNS ====================
// Aldershot
const AldershotRepairs = lazy(() => import("./pages/towns/AldershotRepairs"));
const AldershotServicing = lazy(() => import("./pages/towns/AldershotServicing"));
const AldershotAutomation = lazy(() => import("./pages/towns/AldershotAutomation"));

// Alton
const AltonRepairs = lazy(() => import("./pages/towns/AltonRepairs"));
const AltonServicing = lazy(() => import("./pages/towns/AltonServicing"));
const AltonAutomation = lazy(() => import("./pages/towns/AltonAutomation"));

// Andover
const AndoverRepairs = lazy(() => import("./pages/towns/AndoverRepairs"));
const AndoverServicing = lazy(() => import("./pages/towns/AndoverServicing"));
const AndoverAutomation = lazy(() => import("./pages/towns/AndoverAutomation"));

// Basingstoke
const BasingstokeRepairs = lazy(() => import("./pages/towns/BasingstokeRepairs"));
const BasingstokeServicing = lazy(() => import("./pages/towns/BasingstokeServicing"));
const BasingstokeAutomation = lazy(() => import("./pages/towns/BasingstokeAutomation"));

// Bishops Waltham
const BishopsWalthamRepairs = lazy(() => import("./pages/towns/BishopsWalthamRepairs"));
const BishopsWalthamServicing = lazy(() => import("./pages/towns/BishopsWalthamServicing"));
const BishopsWalthamAutomation = lazy(() => import("./pages/towns/BishopsWalthamAutomation"));

// Chandlers Ford
const ChandlersFordRepairs = lazy(() => import("./pages/towns/ChandlersFordRepairs"));
const ChandlersFordServicing = lazy(() => import("./pages/towns/ChandlersFordServicing"));
const ChandlersFordAutomation = lazy(() => import("./pages/towns/ChandlersFordAutomation"));

// Eastleigh
const EastleighRepairs = lazy(() => import("./pages/towns/EastleighRepairs"));
const EastleighServicing = lazy(() => import("./pages/towns/EastleighServicing"));
const EastleighAutomation = lazy(() => import("./pages/towns/EastleighAutomation"));

// Fareham
const FarehamRepairs = lazy(() => import("./pages/towns/FarehamRepairs"));
const FarehamServicing = lazy(() => import("./pages/towns/FarehamServicing"));
const FarehamAutomation = lazy(() => import("./pages/towns/FarehamAutomation"));

// Farnborough
const FarnboroughRepairs = lazy(() => import("./pages/towns/FarnboroughRepairs"));
const FarnboroughServicing = lazy(() => import("./pages/towns/FarnboroughServicing"));
const FarnboroughAutomation = lazy(() => import("./pages/towns/FarnboroughAutomation"));

// Fleet
const FleetRepairs = lazy(() => import("./pages/towns/FleetRepairs"));
const FleetServicing = lazy(() => import("./pages/towns/FleetServicing"));
const FleetAutomation = lazy(() => import("./pages/towns/FleetAutomation"));

// Fordingbridge
const FordingbridgeRepairs = lazy(() => import("./pages/towns/FordingbridgeRepairs"));
const FordingbridgeServicing = lazy(() => import("./pages/towns/FordingbridgeServicing"));
const FordingbridgeAutomation = lazy(() => import("./pages/towns/FordingbridgeAutomation"));

// Gosport
const GosportRepairs = lazy(() => import("./pages/towns/GosportRepairs"));
const GosportServicing = lazy(() => import("./pages/towns/GosportServicing"));
const GosportAutomation = lazy(() => import("./pages/towns/GosportAutomation"));

// Hartley Wintney
const HartleyWintneyRepairs = lazy(() => import("./pages/towns/HartleyWintneyRepairs"));
const HartleyWintneyServicing = lazy(() => import("./pages/towns/HartleyWintneyServicing"));
const HartleyWintneyAutomation = lazy(() => import("./pages/towns/HartleyWintneyAutomation"));

// Havant
const HavantRepairs = lazy(() => import("./pages/towns/HavantRepairs"));
const HavantServicing = lazy(() => import("./pages/towns/HavantServicing"));
const HavantAutomation = lazy(() => import("./pages/towns/HavantAutomation"));

// Hedge End
const HedgeEndRepairs = lazy(() => import("./pages/towns/HedgeEndRepairs"));
const HedgeEndServicing = lazy(() => import("./pages/towns/HedgeEndServicing"));
const HedgeEndAutomation = lazy(() => import("./pages/towns/HedgeEndAutomation"));

// Hook
const HookRepairs = lazy(() => import("./pages/towns/HookRepairs"));
const HookServicing = lazy(() => import("./pages/towns/HookServicing"));
const HookAutomation = lazy(() => import("./pages/towns/HookAutomation"));

// Lymington
const LymingtonRepairs = lazy(() => import("./pages/towns/LymingtonRepairs"));
const LymingtonServicing = lazy(() => import("./pages/towns/LymingtonServicing"));
const LymingtonAutomation = lazy(() => import("./pages/towns/LymingtonAutomation"));

// New Milton
const NewMiltonRepairs = lazy(() => import("./pages/towns/NewMiltonRepairs"));
const NewMiltonServicing = lazy(() => import("./pages/towns/NewMiltonServicing"));
const NewMiltonAutomation = lazy(() => import("./pages/towns/NewMiltonAutomation"));

// Odiham
const OdihamRepairs = lazy(() => import("./pages/towns/OdihamRepairs"));
const OdihamServicing = lazy(() => import("./pages/towns/OdihamServicing"));
const OdihamAutomation = lazy(() => import("./pages/towns/OdihamAutomation"));

// Petersfield
const PetersfieldRepairs = lazy(() => import("./pages/towns/PetersfieldRepairs"));
const PetersfieldServicing = lazy(() => import("./pages/towns/PetersfieldServicing"));
const PetersfieldAutomation = lazy(() => import("./pages/towns/PetersfieldAutomation"));

// Portsmouth
const PortsmouthRepairs = lazy(() => import("./pages/towns/PortsmouthRepairs"));
const PortsmouthServicing = lazy(() => import("./pages/towns/PortsmouthServicing"));
const PortsmouthAutomation = lazy(() => import("./pages/towns/PortsmouthAutomation"));

// Ringwood
const RingwoodRepairs = lazy(() => import("./pages/towns/RingwoodRepairs"));
const RingwoodServicing = lazy(() => import("./pages/towns/RingwoodServicing"));
const RingwoodAutomation = lazy(() => import("./pages/towns/RingwoodAutomation"));

// Romsey
const RomseyRepairs = lazy(() => import("./pages/towns/RomseyRepairs"));
const RomseyServicing = lazy(() => import("./pages/towns/RomseyServicing"));
const RomseyAutomation = lazy(() => import("./pages/towns/RomseyAutomation"));

// Southampton
const SouthamptonRepairs = lazy(() => import("./pages/towns/SouthamptonRepairs"));
const SouthamptonServicing = lazy(() => import("./pages/towns/SouthamptonServicing"));
const SouthamptonAutomation = lazy(() => import("./pages/towns/SouthamptonAutomation"));

// Waterlooville
const WaterloovilleRepairs = lazy(() => import("./pages/towns/WaterloovilleRepairs"));
const WaterloovilleServicing = lazy(() => import("./pages/towns/WaterloovilleServicing"));
const WaterloovilleAutomation = lazy(() => import("./pages/towns/WaterloovilleAutomation"));

// Whitchurch
const WhitchurchRepairs = lazy(() => import("./pages/towns/WhitchurchRepairs"));
const WhitchurchServicing = lazy(() => import("./pages/towns/WhitchurchServicing"));
const WhitchurchAutomation = lazy(() => import("./pages/towns/WhitchurchAutomation"));

// Winchester
const WinchesterRepairs = lazy(() => import("./pages/towns/WinchesterRepairs"));
const WinchesterServicing = lazy(() => import("./pages/towns/WinchesterServicing"));
const WinchesterAutomation = lazy(() => import("./pages/towns/WinchesterAutomation"));

// Yateley
const YateleyRepairs = lazy(() => import("./pages/towns/YateleyRepairs"));
const YateleyServicing = lazy(() => import("./pages/towns/YateleyServicing"));
const YateleyAutomation = lazy(() => import("./pages/towns/YateleyAutomation"));

// ==================== SURREY TOWNS ====================
// Addlestone
const AddlestoneRepairs = lazy(() => import("./pages/towns/AddlestoneRepairs"));
const AddlestoneServicing = lazy(() => import("./pages/towns/AddlestoneServicing"));
const AddlestoneAutomation = lazy(() => import("./pages/towns/AddlestoneAutomation"));

// Ash
const AshRepairs = lazy(() => import("./pages/towns/AshRepairs"));
const AshServicing = lazy(() => import("./pages/towns/AshServicing"));
const AshAutomation = lazy(() => import("./pages/towns/AshAutomation"));

// Ashtead
const AshteadRepairs = lazy(() => import("./pages/towns/AshteadRepairs"));
const AshteadServicing = lazy(() => import("./pages/towns/AshteadServicing"));
const AshteadAutomation = lazy(() => import("./pages/towns/AshteadAutomation"));

// Bramley
const BramleyRepairs = lazy(() => import("./pages/towns/BramleyRepairs"));
const BramleyServicing = lazy(() => import("./pages/towns/BramleyServicing"));
const BramleyAutomation = lazy(() => import("./pages/towns/BramleyAutomation"));

// Camberley
const CamberleyRepairs = lazy(() => import("./pages/towns/CamberleyRepairs"));
const CamberleyServicing = lazy(() => import("./pages/towns/CamberleyServicing"));
const CamberleyAutomation = lazy(() => import("./pages/towns/CamberleyAutomation"));

// Caterham
const CaterhamRepairs = lazy(() => import("./pages/towns/CaterhamRepairs"));
const CaterhamServicing = lazy(() => import("./pages/towns/CaterhamServicing"));
const CaterhamAutomation = lazy(() => import("./pages/towns/CaterhamAutomation"));

// Chertsey
const ChertseyRepairs = lazy(() => import("./pages/towns/ChertseyRepairs"));
const ChertseyServicing = lazy(() => import("./pages/towns/ChertseyServicing"));
const ChertseyAutomation = lazy(() => import("./pages/towns/ChertseyAutomation"));

// Cobham
const CobhamRepairs = lazy(() => import("./pages/towns/CobhamRepairs"));
const CobhamServicing = lazy(() => import("./pages/towns/CobhamServicing"));
const CobhamAutomation = lazy(() => import("./pages/towns/CobhamAutomation"));

// Cranleigh
const CranleighRepairs = lazy(() => import("./pages/towns/CranleighRepairs"));
const CranleighServicing = lazy(() => import("./pages/towns/CranleighServicing"));
const CranleighAutomation = lazy(() => import("./pages/towns/CranleighAutomation"));

// Dorking
const DorkingRepairs = lazy(() => import("./pages/towns/DorkingRepairs"));
const DorkingServicing = lazy(() => import("./pages/towns/DorkingServicing"));
const DorkingAutomation = lazy(() => import("./pages/towns/DorkingAutomation"));

// Egham
const EghamRepairs = lazy(() => import("./pages/towns/EghamRepairs"));
const EghamServicing = lazy(() => import("./pages/towns/EghamServicing"));
const EghamAutomation = lazy(() => import("./pages/towns/EghamAutomation"));

// Epsom
const EpsomRepairs = lazy(() => import("./pages/towns/EpsomRepairs"));
const EpsomServicing = lazy(() => import("./pages/towns/EpsomServicing"));
const EpsomAutomation = lazy(() => import("./pages/towns/EpsomAutomation"));

// Esher
const EsherRepairs = lazy(() => import("./pages/towns/EsherRepairs"));
const EsherServicing = lazy(() => import("./pages/towns/EsherServicing"));
const EsherAutomation = lazy(() => import("./pages/towns/EsherAutomation"));

// Farnham
const FarnhamRepairs = lazy(() => import("./pages/towns/FarnhamRepairs"));
const FarnhamServicing = lazy(() => import("./pages/towns/FarnhamServicing"));
const FarnhamAutomation = lazy(() => import("./pages/towns/FarnhamAutomation"));

// Godalming
const GodalmingRepairs = lazy(() => import("./pages/towns/GodalmingRepairs"));
const GodalmingServicing = lazy(() => import("./pages/towns/GodalmingServicing"));
const GodalmingAutomation = lazy(() => import("./pages/towns/GodalmingAutomation"));

// Guildford
const GuildfordRepairs = lazy(() => import("./pages/towns/GuildfordRepairs"));
const GuildfordServicing = lazy(() => import("./pages/towns/GuildfordServicing"));
const GuildfordAutomation = lazy(() => import("./pages/towns/GuildfordAutomation"));

// Haslemere
const HaslemereRepairs = lazy(() => import("./pages/towns/HaslemereRepairs"));
const HaslemereServicing = lazy(() => import("./pages/towns/HaslemereServicing"));
const HaslemereAutomation = lazy(() => import("./pages/towns/HaslemereAutomation"));

// Horley
const HorleyRepairs = lazy(() => import("./pages/towns/HorleyRepairs"));
const HorleyServicing = lazy(() => import("./pages/towns/HorleyServicing"));
const HorleyAutomation = lazy(() => import("./pages/towns/HorleyAutomation"));

// Leatherhead
const LeatherheadRepairs = lazy(() => import("./pages/towns/LeatherheadRepairs"));
const LeatherheadServicing = lazy(() => import("./pages/towns/LeatherheadServicing"));
const LeatherheadAutomation = lazy(() => import("./pages/towns/LeatherheadAutomation"));

// Oxted
const OxtedRepairs = lazy(() => import("./pages/towns/OxtedRepairs"));
const OxtedServicing = lazy(() => import("./pages/towns/OxtedServicing"));
const OxtedAutomation = lazy(() => import("./pages/towns/OxtedAutomation"));

// Redhill
const RedhillRepairs = lazy(() => import("./pages/towns/RedhillRepairs"));
const RedhillServicing = lazy(() => import("./pages/towns/RedhillServicing"));
const RedhillAutomation = lazy(() => import("./pages/towns/RedhillAutomation"));

// Reigate
const ReigateRepairs = lazy(() => import("./pages/towns/ReigateRepairs"));
const ReigateServicing = lazy(() => import("./pages/towns/ReigateServicing"));
const ReigateAutomation = lazy(() => import("./pages/towns/ReigateAutomation"));

// Shalford
const ShalfordRepairs = lazy(() => import("./pages/towns/ShalfordRepairs"));
const ShalfordServicing = lazy(() => import("./pages/towns/ShalfordServicing"));
const ShalfordAutomation = lazy(() => import("./pages/towns/ShalfordAutomation"));

// Staines
const StainesRepairs = lazy(() => import("./pages/towns/StainesRepairs"));
const StainesServicing = lazy(() => import("./pages/towns/StainesServicing"));
const StainesAutomation = lazy(() => import("./pages/towns/StainesAutomation"));

// Tongham
const TonghamRepairs = lazy(() => import("./pages/towns/TonghamRepairs"));
const TonghamServicing = lazy(() => import("./pages/towns/TonghamServicing"));
const TonghamAutomation = lazy(() => import("./pages/towns/TonghamAutomation"));

// Virginia Water
const VirginiaWaterRepairs = lazy(() => import("./pages/towns/VirginiaWaterRepairs"));
const VirginiaWaterServicing = lazy(() => import("./pages/towns/VirginiaWaterServicing"));
const VirginiaWaterAutomation = lazy(() => import("./pages/towns/VirginiaWaterAutomation"));

// Walton-on-Thames
const WaltonOnThamesRepairs = lazy(() => import("./pages/towns/WaltonOnThamesRepairs"));
const WaltonOnThamesServicing = lazy(() => import("./pages/towns/WaltonOnThamesServicing"));
const WaltonOnThamesAutomation = lazy(() => import("./pages/towns/WaltonOnThamesAutomation"));

// Weybridge
const WeybridgeRepairs = lazy(() => import("./pages/towns/WeybridgeRepairs"));
const WeybridgeServicing = lazy(() => import("./pages/towns/WeybridgeServicing"));
const WeybridgeAutomation = lazy(() => import("./pages/towns/WeybridgeAutomation"));

// Woking
const WokingRepairs = lazy(() => import("./pages/towns/WokingRepairs"));
const WokingServicing = lazy(() => import("./pages/towns/WokingServicing"));
const WokingAutomation = lazy(() => import("./pages/towns/WokingAutomation"));

const queryClient = new QueryClient();

// Minimal loading fallback for lazy routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);
// Redirect handler for old /towns/:slug URLs to new format
const TownRedirectHandler = () => {
  const { slug } = useParams();
  if (!slug) return <Navigate to="/" replace />;
  
  // Old format: "portsmouth-automation" → New format: "/portsmouth-garage-door-automation"
  // Old format: "portsmouth-repairs" → New format: "/portsmouth-garage-door-repairs"
  // Old format: "portsmouth-servicing" → New format: "/portsmouth-garage-door-servicing"
  const serviceTypes = ["repairs", "servicing", "automation"];
  const matchedService = serviceTypes.find(s => slug.endsWith(`-${s}`));
  
  if (matchedService) {
    const town = slug.replace(`-${matchedService}`, "");
    return <Navigate to={`/${town}-garage-door-${matchedService}`} replace />;
  }
  
  // If no service type matched, redirect to home
  return <Navigate to="/" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/berkshire" element={<Berkshire />} />
              <Route path="/hampshire" element={<Hampshire />} />
              <Route path="/surrey" element={<Surrey />} />
              <Route path="/servicing" element={<Servicing />} />
              <Route path="/repairs" element={<Repairs />} />
              <Route path="/automation" element={<Automation />} />
              <Route path="/smart-controller" element={<SmartController />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/faqs" element={<FAQ />} />
              <Route path="/faq" element={<Navigate to="/faqs" replace />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
              <Route path="/cookie-policy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/towns/:slug" element={<TownRedirectHandler />} />
              
              {/* Admin routes */}
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/admin/blog" element={<ProtectedRoute><BlogAdmin /></ProtectedRoute>} />
              <Route path="/admin/testimonials" element={<ProtectedRoute><TestimonialsAdmin /></ProtectedRoute>} />
              <Route path="/admin/services" element={<ProtectedRoute><ServicesAdmin /></ProtectedRoute>} />
              <Route path="/admin/areas" element={<ProtectedRoute><AreasAdmin /></ProtectedRoute>} />
              <Route path="/admin/content" element={<ProtectedRoute><ContentAdmin /></ProtectedRoute>} />
              
              {/* ==================== BERKSHIRE TOWN PAGES ==================== */}
              {/* Ascot */}
              <Route path="/ascot-garage-door-repairs" element={<AscotRepairs />} />
              <Route path="/ascot-garage-door-servicing" element={<AscotServicing />} />
              <Route path="/ascot-garage-door-automation" element={<AscotAutomation />} />
              
              {/* Bracknell */}
              <Route path="/bracknell-garage-door-repairs" element={<BracknellRepairs />} />
              <Route path="/bracknell-garage-door-servicing" element={<BracknellServicing />} />
              <Route path="/bracknell-garage-door-automation" element={<BracknellAutomation />} />
              
              {/* Burnham */}
              <Route path="/burnham-garage-door-repairs" element={<BurnhamRepairs />} />
              <Route path="/burnham-garage-door-servicing" element={<BurnhamServicing />} />
              <Route path="/burnham-garage-door-automation" element={<BurnhamAutomation />} />
              
              {/* Calcot */}
              <Route path="/calcot-garage-door-repairs" element={<CalcotRepairs />} />
              <Route path="/calcot-garage-door-servicing" element={<CalcotServicing />} />
              <Route path="/calcot-garage-door-automation" element={<CalcotAutomation />} />
              
              {/* Caversham */}
              <Route path="/caversham-garage-door-repairs" element={<CavershamRepairs />} />
              <Route path="/caversham-garage-door-servicing" element={<CavershamServicing />} />
              <Route path="/caversham-garage-door-automation" element={<CavershamAutomation />} />
              
              {/* Cookham */}
              <Route path="/cookham-garage-door-repairs" element={<CookhamRepairs />} />
              <Route path="/cookham-garage-door-servicing" element={<CookhamServicing />} />
              <Route path="/cookham-garage-door-automation" element={<CookhamAutomation />} />
              
              {/* Crowthorne */}
              <Route path="/crowthorne-garage-door-repairs" element={<CrowthorneRepairs />} />
              <Route path="/crowthorne-garage-door-servicing" element={<CrowthorneServicing />} />
              <Route path="/crowthorne-garage-door-automation" element={<CrowthorneAutomation />} />
              
              {/* Datchet */}
              <Route path="/datchet-garage-door-repairs" element={<DatchetRepairs />} />
              <Route path="/datchet-garage-door-servicing" element={<DatchetServicing />} />
              <Route path="/datchet-garage-door-automation" element={<DatchetAutomation />} />
              
              {/* Earley */}
              <Route path="/earley-garage-door-repairs" element={<EarleyRepairs />} />
              <Route path="/earley-garage-door-servicing" element={<EarleyServicing />} />
              <Route path="/earley-garage-door-automation" element={<EarleyAutomation />} />
              
              {/* Finchampstead */}
              <Route path="/finchampstead-garage-door-repairs" element={<FinchampsteadRepairs />} />
              <Route path="/finchampstead-garage-door-servicing" element={<FinchampsteadServicing />} />
              <Route path="/finchampstead-garage-door-automation" element={<FinchampsteadAutomation />} />
              
              {/* Hungerford */}
              <Route path="/hungerford-garage-door-repairs" element={<HungerfordRepairs />} />
              <Route path="/hungerford-garage-door-servicing" element={<HungerfordServicing />} />
              <Route path="/hungerford-garage-door-automation" element={<HungerfordAutomation />} />
              
              {/* Iver */}
              <Route path="/iver-garage-door-repairs" element={<IverRepairs />} />
              <Route path="/iver-garage-door-servicing" element={<IverServicing />} />
              <Route path="/iver-garage-door-automation" element={<IverAutomation />} />
              
              {/* Langley */}
              <Route path="/langley-garage-door-repairs" element={<LangleyRepairs />} />
              <Route path="/langley-garage-door-servicing" element={<LangleyServicing />} />
              <Route path="/langley-garage-door-automation" element={<LangleyAutomation />} />
              
              {/* Maidenhead */}
              <Route path="/maidenhead-garage-door-repairs" element={<MaidenheadRepairs />} />
              <Route path="/maidenhead-garage-door-servicing" element={<MaidenheadServicing />} />
              <Route path="/maidenhead-garage-door-automation" element={<MaidenheadAutomation />} />
              
              {/* Marlow */}
              <Route path="/marlow-garage-door-repairs" element={<MarlowRepairs />} />
              <Route path="/marlow-garage-door-servicing" element={<MarlowServicing />} />
              <Route path="/marlow-garage-door-automation" element={<MarlowAutomation />} />
              
              {/* Newbury */}
              <Route path="/newbury-garage-door-repairs" element={<NewburyRepairs />} />
              <Route path="/newbury-garage-door-servicing" element={<NewburyServicing />} />
              <Route path="/newbury-garage-door-automation" element={<NewburyAutomation />} />
              
              {/* Pangbourne */}
              <Route path="/pangbourne-garage-door-repairs" element={<PangbourneRepairs />} />
              <Route path="/pangbourne-garage-door-servicing" element={<PangbourneServicing />} />
              <Route path="/pangbourne-garage-door-automation" element={<PangbourneAutomation />} />
              
              {/* Reading */}
              <Route path="/reading-garage-door-repairs" element={<ReadingRepairs />} />
              <Route path="/reading-garage-door-servicing" element={<ReadingServicing />} />
              <Route path="/reading-garage-door-automation" element={<ReadingAutomation />} />
              
              {/* Sandhurst */}
              <Route path="/sandhurst-garage-door-repairs" element={<SandhurstRepairs />} />
              <Route path="/sandhurst-garage-door-servicing" element={<SandhurstServicing />} />
              <Route path="/sandhurst-garage-door-automation" element={<SandhurstAutomation />} />
              
              {/* Slough */}
              <Route path="/slough-garage-door-repairs" element={<SloughRepairs />} />
              <Route path="/slough-garage-door-servicing" element={<SloughServicing />} />
              <Route path="/slough-garage-door-automation" element={<SloughAutomation />} />
              
              {/* Sunningdale */}
              <Route path="/sunningdale-garage-door-repairs" element={<SunningdaleRepairs />} />
              <Route path="/sunningdale-garage-door-servicing" element={<SunningdaleServicing />} />
              <Route path="/sunningdale-garage-door-automation" element={<SunningdaleAutomation />} />
              
              {/* Sunninghill */}
              <Route path="/sunninghill-garage-door-repairs" element={<SunninghillRepairs />} />
              <Route path="/sunninghill-garage-door-servicing" element={<SunninghillServicing />} />
              <Route path="/sunninghill-garage-door-automation" element={<SunninghillAutomation />} />
              
              {/* Thatcham */}
              <Route path="/thatcham-garage-door-repairs" element={<ThatchamRepairs />} />
              <Route path="/thatcham-garage-door-servicing" element={<ThatchamServicing />} />
              <Route path="/thatcham-garage-door-automation" element={<ThatchamAutomation />} />
              
              {/* Theale */}
              <Route path="/theale-garage-door-repairs" element={<ThealeRepairs />} />
              <Route path="/theale-garage-door-servicing" element={<ThealeServicing />} />
              <Route path="/theale-garage-door-automation" element={<ThealeAutomation />} />
              
              {/* Tilehurst */}
              <Route path="/tilehurst-garage-door-repairs" element={<TilehurstRepairs />} />
              <Route path="/tilehurst-garage-door-servicing" element={<TilehurstServicing />} />
              <Route path="/tilehurst-garage-door-automation" element={<TilehurstAutomation />} />
              
              {/* Twyford */}
              <Route path="/twyford-garage-door-repairs" element={<TwyfordRepairs />} />
              <Route path="/twyford-garage-door-servicing" element={<TwyfordServicing />} />
              <Route path="/twyford-garage-door-automation" element={<TwyfordAutomation />} />
              
              {/* Warfield */}
              <Route path="/warfield-garage-door-repairs" element={<WarfieldRepairs />} />
              <Route path="/warfield-garage-door-servicing" element={<WarfieldServicing />} />
              <Route path="/warfield-garage-door-automation" element={<WarfieldAutomation />} />
              
              {/* Windsor */}
              <Route path="/windsor-garage-door-repairs" element={<WindsorRepairs />} />
              <Route path="/windsor-garage-door-servicing" element={<WindsorServicing />} />
              <Route path="/windsor-garage-door-automation" element={<WindsorAutomation />} />
              
              {/* Wokingham */}
              <Route path="/wokingham-garage-door-repairs" element={<WokinghamRepairs />} />
              <Route path="/wokingham-garage-door-servicing" element={<WokinghamServicing />} />
              <Route path="/wokingham-garage-door-automation" element={<WokinghamAutomation />} />
              
              {/* Woodley */}
              <Route path="/woodley-garage-door-repairs" element={<WoodleyRepairs />} />
              <Route path="/woodley-garage-door-servicing" element={<WoodleyServicing />} />
              <Route path="/woodley-garage-door-automation" element={<WoodleyAutomation />} />
              
              {/* ==================== HAMPSHIRE TOWN PAGES ==================== */}
              {/* Aldershot */}
              <Route path="/aldershot-garage-door-repairs" element={<AldershotRepairs />} />
              <Route path="/aldershot-garage-door-servicing" element={<AldershotServicing />} />
              <Route path="/aldershot-garage-door-automation" element={<AldershotAutomation />} />
              
              {/* Alton */}
              <Route path="/alton-garage-door-repairs" element={<AltonRepairs />} />
              <Route path="/alton-garage-door-servicing" element={<AltonServicing />} />
              <Route path="/alton-garage-door-automation" element={<AltonAutomation />} />
              
              {/* Andover */}
              <Route path="/andover-garage-door-repairs" element={<AndoverRepairs />} />
              <Route path="/andover-garage-door-servicing" element={<AndoverServicing />} />
              <Route path="/andover-garage-door-automation" element={<AndoverAutomation />} />
              
              {/* Basingstoke */}
              <Route path="/basingstoke-garage-door-repairs" element={<BasingstokeRepairs />} />
              <Route path="/basingstoke-garage-door-servicing" element={<BasingstokeServicing />} />
              <Route path="/basingstoke-garage-door-automation" element={<BasingstokeAutomation />} />
              
              {/* Bishops Waltham */}
              <Route path="/bishops-waltham-garage-door-repairs" element={<BishopsWalthamRepairs />} />
              <Route path="/bishops-waltham-garage-door-servicing" element={<BishopsWalthamServicing />} />
              <Route path="/bishops-waltham-garage-door-automation" element={<BishopsWalthamAutomation />} />
              
              {/* Chandlers Ford */}
              <Route path="/chandlers-ford-garage-door-repairs" element={<ChandlersFordRepairs />} />
              <Route path="/chandlers-ford-garage-door-servicing" element={<ChandlersFordServicing />} />
              <Route path="/chandlers-ford-garage-door-automation" element={<ChandlersFordAutomation />} />
              
              {/* Eastleigh */}
              <Route path="/eastleigh-garage-door-repairs" element={<EastleighRepairs />} />
              <Route path="/eastleigh-garage-door-servicing" element={<EastleighServicing />} />
              <Route path="/eastleigh-garage-door-automation" element={<EastleighAutomation />} />
              
              {/* Fareham */}
              <Route path="/fareham-garage-door-repairs" element={<FarehamRepairs />} />
              <Route path="/fareham-garage-door-servicing" element={<FarehamServicing />} />
              <Route path="/fareham-garage-door-automation" element={<FarehamAutomation />} />
              
              {/* Farnborough */}
              <Route path="/farnborough-garage-door-repairs" element={<FarnboroughRepairs />} />
              <Route path="/farnborough-garage-door-servicing" element={<FarnboroughServicing />} />
              <Route path="/farnborough-garage-door-automation" element={<FarnboroughAutomation />} />
              
              {/* Fleet */}
              <Route path="/fleet-garage-door-repairs" element={<FleetRepairs />} />
              <Route path="/fleet-garage-door-servicing" element={<FleetServicing />} />
              <Route path="/fleet-garage-door-automation" element={<FleetAutomation />} />
              
              {/* Fordingbridge */}
              <Route path="/fordingbridge-garage-door-repairs" element={<FordingbridgeRepairs />} />
              <Route path="/fordingbridge-garage-door-servicing" element={<FordingbridgeServicing />} />
              <Route path="/fordingbridge-garage-door-automation" element={<FordingbridgeAutomation />} />
              
              {/* Gosport */}
              <Route path="/gosport-garage-door-repairs" element={<GosportRepairs />} />
              <Route path="/gosport-garage-door-servicing" element={<GosportServicing />} />
              <Route path="/gosport-garage-door-automation" element={<GosportAutomation />} />
              
              {/* Hartley Wintney */}
              <Route path="/hartley-wintney-garage-door-repairs" element={<HartleyWintneyRepairs />} />
              <Route path="/hartley-wintney-garage-door-servicing" element={<HartleyWintneyServicing />} />
              <Route path="/hartley-wintney-garage-door-automation" element={<HartleyWintneyAutomation />} />
              
              {/* Havant */}
              <Route path="/havant-garage-door-repairs" element={<HavantRepairs />} />
              <Route path="/havant-garage-door-servicing" element={<HavantServicing />} />
              <Route path="/havant-garage-door-automation" element={<HavantAutomation />} />
              
              {/* Hedge End */}
              <Route path="/hedge-end-garage-door-repairs" element={<HedgeEndRepairs />} />
              <Route path="/hedge-end-garage-door-servicing" element={<HedgeEndServicing />} />
              <Route path="/hedge-end-garage-door-automation" element={<HedgeEndAutomation />} />
              
              {/* Hook */}
              <Route path="/hook-garage-door-repairs" element={<HookRepairs />} />
              <Route path="/hook-garage-door-servicing" element={<HookServicing />} />
              <Route path="/hook-garage-door-automation" element={<HookAutomation />} />
              
              {/* Lymington */}
              <Route path="/lymington-garage-door-repairs" element={<LymingtonRepairs />} />
              <Route path="/lymington-garage-door-servicing" element={<LymingtonServicing />} />
              <Route path="/lymington-garage-door-automation" element={<LymingtonAutomation />} />
              
              {/* New Milton */}
              <Route path="/new-milton-garage-door-repairs" element={<NewMiltonRepairs />} />
              <Route path="/new-milton-garage-door-servicing" element={<NewMiltonServicing />} />
              <Route path="/new-milton-garage-door-automation" element={<NewMiltonAutomation />} />
              
              {/* Odiham */}
              <Route path="/odiham-garage-door-repairs" element={<OdihamRepairs />} />
              <Route path="/odiham-garage-door-servicing" element={<OdihamServicing />} />
              <Route path="/odiham-garage-door-automation" element={<OdihamAutomation />} />
              
              {/* Petersfield */}
              <Route path="/petersfield-garage-door-repairs" element={<PetersfieldRepairs />} />
              <Route path="/petersfield-garage-door-servicing" element={<PetersfieldServicing />} />
              <Route path="/petersfield-garage-door-automation" element={<PetersfieldAutomation />} />
              
              {/* Portsmouth */}
              <Route path="/portsmouth-garage-door-repairs" element={<PortsmouthRepairs />} />
              <Route path="/portsmouth-garage-door-servicing" element={<PortsmouthServicing />} />
              <Route path="/portsmouth-garage-door-automation" element={<PortsmouthAutomation />} />
              
              {/* Ringwood */}
              <Route path="/ringwood-garage-door-repairs" element={<RingwoodRepairs />} />
              <Route path="/ringwood-garage-door-servicing" element={<RingwoodServicing />} />
              <Route path="/ringwood-garage-door-automation" element={<RingwoodAutomation />} />
              
              {/* Romsey */}
              <Route path="/romsey-garage-door-repairs" element={<RomseyRepairs />} />
              <Route path="/romsey-garage-door-servicing" element={<RomseyServicing />} />
              <Route path="/romsey-garage-door-automation" element={<RomseyAutomation />} />
              
              {/* Southampton */}
              <Route path="/southampton-garage-door-repairs" element={<SouthamptonRepairs />} />
              <Route path="/southampton-garage-door-servicing" element={<SouthamptonServicing />} />
              <Route path="/southampton-garage-door-automation" element={<SouthamptonAutomation />} />
              
              {/* Waterlooville */}
              <Route path="/waterlooville-garage-door-repairs" element={<WaterloovilleRepairs />} />
              <Route path="/waterlooville-garage-door-servicing" element={<WaterloovilleServicing />} />
              <Route path="/waterlooville-garage-door-automation" element={<WaterloovilleAutomation />} />
              
              {/* Whitchurch */}
              <Route path="/whitchurch-garage-door-repairs" element={<WhitchurchRepairs />} />
              <Route path="/whitchurch-garage-door-servicing" element={<WhitchurchServicing />} />
              <Route path="/whitchurch-garage-door-automation" element={<WhitchurchAutomation />} />
              
              {/* Winchester */}
              <Route path="/winchester-garage-door-repairs" element={<WinchesterRepairs />} />
              <Route path="/winchester-garage-door-servicing" element={<WinchesterServicing />} />
              <Route path="/winchester-garage-door-automation" element={<WinchesterAutomation />} />
              
              {/* Yateley */}
              <Route path="/yateley-garage-door-repairs" element={<YateleyRepairs />} />
              <Route path="/yateley-garage-door-servicing" element={<YateleyServicing />} />
              <Route path="/yateley-garage-door-automation" element={<YateleyAutomation />} />
              
              {/* ==================== SURREY TOWN PAGES ==================== */}
              {/* Addlestone */}
              <Route path="/addlestone-garage-door-repairs" element={<AddlestoneRepairs />} />
              <Route path="/addlestone-garage-door-servicing" element={<AddlestoneServicing />} />
              <Route path="/addlestone-garage-door-automation" element={<AddlestoneAutomation />} />
              
              {/* Ash */}
              <Route path="/ash-garage-door-repairs" element={<AshRepairs />} />
              <Route path="/ash-garage-door-servicing" element={<AshServicing />} />
              <Route path="/ash-garage-door-automation" element={<AshAutomation />} />
              
              {/* Ashtead */}
              <Route path="/ashtead-garage-door-repairs" element={<AshteadRepairs />} />
              <Route path="/ashtead-garage-door-servicing" element={<AshteadServicing />} />
              <Route path="/ashtead-garage-door-automation" element={<AshteadAutomation />} />
              
              {/* Bramley */}
              <Route path="/bramley-garage-door-repairs" element={<BramleyRepairs />} />
              <Route path="/bramley-garage-door-servicing" element={<BramleyServicing />} />
              <Route path="/bramley-garage-door-automation" element={<BramleyAutomation />} />
              
              {/* Camberley */}
              <Route path="/camberley-garage-door-repairs" element={<CamberleyRepairs />} />
              <Route path="/camberley-garage-door-servicing" element={<CamberleyServicing />} />
              <Route path="/camberley-garage-door-automation" element={<CamberleyAutomation />} />
              
              {/* Caterham */}
              <Route path="/caterham-garage-door-repairs" element={<CaterhamRepairs />} />
              <Route path="/caterham-garage-door-servicing" element={<CaterhamServicing />} />
              <Route path="/caterham-garage-door-automation" element={<CaterhamAutomation />} />
              
              {/* Chertsey */}
              <Route path="/chertsey-garage-door-repairs" element={<ChertseyRepairs />} />
              <Route path="/chertsey-garage-door-servicing" element={<ChertseyServicing />} />
              <Route path="/chertsey-garage-door-automation" element={<ChertseyAutomation />} />
              
              {/* Cobham */}
              <Route path="/cobham-garage-door-repairs" element={<CobhamRepairs />} />
              <Route path="/cobham-garage-door-servicing" element={<CobhamServicing />} />
              <Route path="/cobham-garage-door-automation" element={<CobhamAutomation />} />
              
              {/* Cranleigh */}
              <Route path="/cranleigh-garage-door-repairs" element={<CranleighRepairs />} />
              <Route path="/cranleigh-garage-door-servicing" element={<CranleighServicing />} />
              <Route path="/cranleigh-garage-door-automation" element={<CranleighAutomation />} />
              
              {/* Dorking */}
              <Route path="/dorking-garage-door-repairs" element={<DorkingRepairs />} />
              <Route path="/dorking-garage-door-servicing" element={<DorkingServicing />} />
              <Route path="/dorking-garage-door-automation" element={<DorkingAutomation />} />
              
              {/* Egham */}
              <Route path="/egham-garage-door-repairs" element={<EghamRepairs />} />
              <Route path="/egham-garage-door-servicing" element={<EghamServicing />} />
              <Route path="/egham-garage-door-automation" element={<EghamAutomation />} />
              
              {/* Epsom */}
              <Route path="/epsom-garage-door-repairs" element={<EpsomRepairs />} />
              <Route path="/epsom-garage-door-servicing" element={<EpsomServicing />} />
              <Route path="/epsom-garage-door-automation" element={<EpsomAutomation />} />
              
              {/* Esher */}
              <Route path="/esher-garage-door-repairs" element={<EsherRepairs />} />
              <Route path="/esher-garage-door-servicing" element={<EsherServicing />} />
              <Route path="/esher-garage-door-automation" element={<EsherAutomation />} />
              
              {/* Farnham */}
              <Route path="/farnham-garage-door-repairs" element={<FarnhamRepairs />} />
              <Route path="/farnham-garage-door-servicing" element={<FarnhamServicing />} />
              <Route path="/farnham-garage-door-automation" element={<FarnhamAutomation />} />
              
              {/* Godalming */}
              <Route path="/godalming-garage-door-repairs" element={<GodalmingRepairs />} />
              <Route path="/godalming-garage-door-servicing" element={<GodalmingServicing />} />
              <Route path="/godalming-garage-door-automation" element={<GodalmingAutomation />} />
              
              {/* Guildford */}
              <Route path="/guildford-garage-door-repairs" element={<GuildfordRepairs />} />
              <Route path="/guildford-garage-door-servicing" element={<GuildfordServicing />} />
              <Route path="/guildford-garage-door-automation" element={<GuildfordAutomation />} />
              
              {/* Haslemere */}
              <Route path="/haslemere-garage-door-repairs" element={<HaslemereRepairs />} />
              <Route path="/haslemere-garage-door-servicing" element={<HaslemereServicing />} />
              <Route path="/haslemere-garage-door-automation" element={<HaslemereAutomation />} />
              
              {/* Horley */}
              <Route path="/horley-garage-door-repairs" element={<HorleyRepairs />} />
              <Route path="/horley-garage-door-servicing" element={<HorleyServicing />} />
              <Route path="/horley-garage-door-automation" element={<HorleyAutomation />} />
              
              {/* Leatherhead */}
              <Route path="/leatherhead-garage-door-repairs" element={<LeatherheadRepairs />} />
              <Route path="/leatherhead-garage-door-servicing" element={<LeatherheadServicing />} />
              <Route path="/leatherhead-garage-door-automation" element={<LeatherheadAutomation />} />
              
              {/* Oxted */}
              <Route path="/oxted-garage-door-repairs" element={<OxtedRepairs />} />
              <Route path="/oxted-garage-door-servicing" element={<OxtedServicing />} />
              <Route path="/oxted-garage-door-automation" element={<OxtedAutomation />} />
              
              {/* Redhill */}
              <Route path="/redhill-garage-door-repairs" element={<RedhillRepairs />} />
              <Route path="/redhill-garage-door-servicing" element={<RedhillServicing />} />
              <Route path="/redhill-garage-door-automation" element={<RedhillAutomation />} />
              
              {/* Reigate */}
              <Route path="/reigate-garage-door-repairs" element={<ReigateRepairs />} />
              <Route path="/reigate-garage-door-servicing" element={<ReigateServicing />} />
              <Route path="/reigate-garage-door-automation" element={<ReigateAutomation />} />
              
              {/* Shalford */}
              <Route path="/shalford-garage-door-repairs" element={<ShalfordRepairs />} />
              <Route path="/shalford-garage-door-servicing" element={<ShalfordServicing />} />
              <Route path="/shalford-garage-door-automation" element={<ShalfordAutomation />} />
              
              {/* Staines */}
              <Route path="/staines-garage-door-repairs" element={<StainesRepairs />} />
              <Route path="/staines-garage-door-servicing" element={<StainesServicing />} />
              <Route path="/staines-garage-door-automation" element={<StainesAutomation />} />
              
              {/* Tongham */}
              <Route path="/tongham-garage-door-repairs" element={<TonghamRepairs />} />
              <Route path="/tongham-garage-door-servicing" element={<TonghamServicing />} />
              <Route path="/tongham-garage-door-automation" element={<TonghamAutomation />} />
              
              {/* Virginia Water */}
              <Route path="/virginia-water-garage-door-repairs" element={<VirginiaWaterRepairs />} />
              <Route path="/virginia-water-garage-door-servicing" element={<VirginiaWaterServicing />} />
              <Route path="/virginia-water-garage-door-automation" element={<VirginiaWaterAutomation />} />
              
              {/* Walton-on-Thames */}
              <Route path="/walton-on-thames-garage-door-repairs" element={<WaltonOnThamesRepairs />} />
              <Route path="/walton-on-thames-garage-door-servicing" element={<WaltonOnThamesServicing />} />
              <Route path="/walton-on-thames-garage-door-automation" element={<WaltonOnThamesAutomation />} />
              
              {/* Weybridge */}
              <Route path="/weybridge-garage-door-repairs" element={<WeybridgeRepairs />} />
              <Route path="/weybridge-garage-door-servicing" element={<WeybridgeServicing />} />
              <Route path="/weybridge-garage-door-automation" element={<WeybridgeAutomation />} />
              
              {/* Woking */}
              <Route path="/woking-garage-door-repairs" element={<WokingRepairs />} />
              <Route path="/woking-garage-door-servicing" element={<WokingServicing />} />
              <Route path="/woking-garage-door-automation" element={<WokingAutomation />} />
              
              {/* Redirects */}
              <Route path="/help-advice" element={<Navigate to="/faqs" replace />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/home" element={<Index />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          <Chatbot />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
